/**
 * The card (spec 3): eighteen lines in six bands, in card order. The developer site is organized by it:
 * the sidebar, the card on the home page, and one page per line.
 */
import type { CardLine } from "@bash/shared/platform";

export type Band = "setup" | "work" | "see" | "knows" | "customers" | "run";

export const BANDS: ReadonlyArray<{
  id: Band;
  label: string;
  lines: readonly CardLine[];
}> = [
  { id: "setup", label: "Set up", lines: ["init", "deploy", "test"] },
  { id: "work", label: "Send work", lines: ["ask", "routines", "answer"] },
  {
    id: "see",
    label: "Follow progress",
    lines: ["watch", "talk", "events", "files"],
  },
  { id: "knows", label: "Apps and memory", lines: ["connect", "memory"] },
  {
    id: "customers",
    label: "Your users",
    lines: ["embed", "users", "usage"],
  },
  { id: "run", label: "Access and keys", lines: ["keys", "secrets", "mcp"] },
];

/** The eighteen lines, in card order. */
export const LINES: readonly CardLine[] = BANDS.flatMap((band) => band.lines);

/** What each line does, in the card's own words. */
export const LINE_TITLE: Record<CardLine, string> = {
  init: "Describe a job in plain words, and get a first draft of your agent's setup.",
  deploy:
    "Publish a new version of your agent's setup, and roll back if needed.",
  test: "Run your test scenarios against a version before real users see it.",
  ask: "Send a job in plain words, for one user, many users or your company.",
  routines: "Run a job on a schedule, or each time your system calls a URL.",
  answer:
    "Reply when your agent needs an approval, an answer or your tool's result.",
  watch: "Show your agent's screen live, and let a person take control.",
  talk: "Start a live voice call between a user and your agent.",
  events:
    "Follow each step your agent takes, live or afterwards, or as webhooks.",
  files: "Upload files for your agent, and download the files it makes.",
  connect: "Let a user connect Gmail, Slack, Notion and other apps.",
  memory: "Read, add and delete what your agent remembers about each user.",
  embed: "Put your agent inside your app, for each signed-in user.",
  users: "Manage your users: profiles, spending caps, export and erasing.",
  usage: "See what each job and user cost, and set caps and alerts.",
  keys: "Create, roll and revoke your test and live API keys.",
  secrets: "Store values your agent uses but never shows, such as tokens.",
  mcp: "Use Vestra from any editor or agent that speaks MCP.",
};

export function isLine(value: string): value is CardLine {
  return (LINES as readonly string[]).includes(value);
}

export function bandOf(line: CardLine): (typeof BANDS)[number] {
  const band = BANDS.find((b) => b.lines.includes(line));
  if (!band) throw new Error(`${line} is on no band of the card.`);
  return band;
}

/** The five doors every line can be reached through (spec 3), in the card page's order. */
export type Door = "ts" | "py" | "cli" | "http" | "mcp";
export const DOOR_LABEL: Record<Door, string> = {
  ts: "TypeScript",
  py: "Python",
  cli: "CLI",
  http: "HTTP",
  mcp: "MCP",
};

/**
 * The doors the site shows (Review Focus 2), in the card's order. Each joined once its package was on
 * staging and its samples were checked in CI; sdks/samples/README.md says by what.
 */
export const SHIPPED_DOORS: readonly Door[] = [
  "ts",
  "py",
  "cli",
  "http",
  "mcp",
];

/** One door of one line: a checked sample (sdks/samples/<door>/<sample>), or the plain reason the door is closed. */
export type DoorDoc = { sample: string } | { note: string };

/**
 * Something worth knowing on a line's page: a title, and the site's own copy (`code` and [links](/developers/ask)),
 * its paragraphs apart by a blank line.
 */
export interface Note {
  title: string;
  body: string;
}

/** A line's page: what it is, its doors, and what is worth knowing. */
export interface LineDoc {
  lede: string;
  doors: Partial<Record<Door, DoorDoc>>;
  notes: readonly Note[];
}
